import { createReducer, on } from '@ngrx/store';
import { PendingVideoUploadResponse } from '@pixacare/dtos';
import { ActionStatus } from '@pixacare/shared';
import { lifeCycleActions } from '../life-cycle/life-cycle.actions';
import { SequenceUpload } from './models/sequence-upload';
import { SequenceUploadReference } from './models/sequence-upload-reference';
import { VideoUploadEntry } from './models/video-upload-entry';
import { VideoUploadReference } from './models/video-upload-reference';
import { groupSessionsBySequence } from './utils/group-sessions-by-sequence';
import { videoUploadsActions } from './video-uploads.actions';
import { initialVideoUploadsState, VideoUploadsState } from './video-uploads.state';

// The server only knows a session is still ongoing — its local File bytes are gone after a
// reload, so every hydrated video lands as INTERRUPTION. Sessions are grouped by their sequence;
// the patient name (title) and creation date aren't in the session payload, so they start empty
// and are filled by `setSequenceUploadInfo` once the sequence is fetched.
// A hydrated session is keyed by its server token — unique and already known, so it doubles as the
// video's stable store id (no id to generate in the reducer).
const sessionsToSequenceUpload = (
  clientCode: string,
  sequenceId: number,
  sessions: PendingVideoUploadResponse[],
): SequenceUpload => ({
  clientCode,
  sequenceId,
  title: '',
  videos: sessions.reduce<Record<string, VideoUploadEntry>>((videos, session) => {
    videos[session.sessionToken] = {
      videoId: session.sessionToken,
      fileName: session.originalFileName,
      fileSize: session.fileSize,
      lastModified: session.lastModified,
      sessionToken: session.sessionToken,
      status: ActionStatus.INTERRUPTION,
      syncedBytes: 0,
    };
    return videos;
  }, {}),
});

const patchSequence = (
  state: VideoUploadsState,
  { clientCode, sequenceId }: SequenceUploadReference,
  patch: Partial<SequenceUpload>,
): VideoUploadsState => {
  const sequence = state[clientCode]?.[sequenceId];
  if (!sequence) {
    return state;
  }
  return {
    ...state,
    [clientCode]: { ...state[clientCode], [sequenceId]: { ...sequence, ...patch } },
  };
};

const patchVideo = (
  state: VideoUploadsState,
  reference: VideoUploadReference,
  patch: Partial<VideoUploadEntry>,
): VideoUploadsState => {
  const sequence = state[reference.clientCode]?.[reference.sequenceId];
  const video = sequence?.videos[reference.videoId];
  if (!video) {
    return state;
  }
  return patchSequence(state, reference, {
    videos: { ...sequence.videos, [reference.videoId]: { ...video, ...patch } },
  });
};

export const videoUploadsReducer = createReducer(
  initialVideoUploadsState,

  on(lifeCycleActions.resetState, () => initialVideoUploadsState),

  // Hydrate the store with the server's ongoing sessions as interrupted uploads. Only seeds
  // sequences not already tracked, so a live upload in progress is never clobbered.
  on(videoUploadsActions.loadUploadSessionsSuccess, (state, { clientCode, sessions }): VideoUploadsState => {
    const trackedSequences = state[clientCode] ?? {};

    const seededSequences = Object.entries(groupSessionsBySequence(sessions)).reduce<Record<number, SequenceUpload>>(
      (sequences, [sequenceId, sequenceSessions]) => {
        const numericSequenceId = Number(sequenceId);
        if (!trackedSequences[numericSequenceId]) {
          sequences[numericSequenceId] = sessionsToSequenceUpload(clientCode, numericSequenceId, sequenceSessions);
        }
        return sequences;
      },
      {},
    );

    // Nothing new to hydrate: keep the same state reference so no selector recomputes and the
    // sync-center doesn't re-render — this action fires once per client the user works with.
    if (Object.keys(seededSequences).length === 0) {
      return state;
    }

    return { ...state, [clientCode]: { ...trackedSequences, ...seededSequences } };
  }),

  on(
    videoUploadsActions.uploadSequenceVideos,
    (state, { clientCode, sequenceId, title, createdOn, videos }): VideoUploadsState => {
      const batchVideos = videos.reduce<Record<string, VideoUploadEntry>>((batch, { videoId, file, previewUrl }) => {
        batch[videoId] = {
          videoId,
          fileName: file.name,
          fileSize: file.size,
          previewUrl,
          status: ActionStatus.WAITING,
          syncedBytes: 0,
        };
        return batch;
      }, {});

      return {
        ...state,
        [clientCode]: {
          ...state[clientCode],
          [sequenceId]: {
            clientCode,
            sequenceId,
            title,
            createdOn,
            // Merge onto any videos already tracked for this sequence so a new batch never drops the
            // existing entries (e.g. an interrupted upload) — each video has its own unique id.
            videos: { ...(state[clientCode]?.[sequenceId]?.videos ?? {}), ...batchVideos },
          },
        },
      };
    },
  ),

  // Removes a single video; when it was the sequence's last one, the sequence goes too.
  on(videoUploadsActions.removeVideoUpload, (state, { clientCode, sequenceId, videoId }): VideoUploadsState => {
    const sequence = state[clientCode]?.[sequenceId];
    if (!sequence?.videos[videoId]) {
      return state;
    }
    const remainingVideos = { ...sequence.videos };
    delete remainingVideos[videoId];
    if (Object.keys(remainingVideos).length > 0) {
      return patchSequence(state, { clientCode, sequenceId }, { videos: remainingVideos });
    }

    // A client left with no sequence keeps an empty entry: every consumer flattens the state, so
    // pruning it would be work nothing can observe.
    const remainingSequences = { ...state[clientCode] };
    delete remainingSequences[sequenceId];
    return { ...state, [clientCode]: remainingSequences };
  }),

  // Fills a hydrated interrupted sequence with its fetched patient name and creation date.
  on(videoUploadsActions.setSequenceUploadInfo, (state, { clientCode, sequenceId, title, createdOn }) =>
    patchSequence(state, { clientCode, sequenceId }, { title, createdOn }),
  ),

  // Re-picking a file for an interrupted upload resets that video to a fresh WAITING state and
  // restores its preview from the re-provided file.
  on(videoUploadsActions.resumeVideoUpload, (state, { clientCode, sequenceId, videoId, previewUrl }) =>
    patchVideo(
      state,
      { clientCode, sequenceId, videoId },
      {
        status: ActionStatus.WAITING,
        syncedBytes: 0,
        remainingSeconds: undefined,
        error: undefined,
        previewUrl,
      },
    ),
  ),

  on(
    videoUploadsActions.updateVideoUploadProgress,
    (state, { clientCode, sequenceId, videoId, syncedBytes, remainingSeconds }) =>
      patchVideo(
        state,
        { clientCode, sequenceId, videoId },
        {
          // Not uploading until its first byte left the machine: the batch reports every video as
          // soon as it is handed over, but their parts queue behind whatever is already uploading.
          status: syncedBytes > 0 ? ActionStatus.IN_PROGRESS : ActionStatus.WAITING,
          syncedBytes,
          remainingSeconds,
        },
      ),
  ),

  on(videoUploadsActions.videoUploadSuccess, (state, { clientCode, sequenceId, videoId }) => {
    const video = state[clientCode]?.[sequenceId]?.videos[videoId];
    return patchVideo(
      state,
      { clientCode, sequenceId, videoId },
      {
        status: ActionStatus.SUCCESS,
        // Snap the bar to 100% even if the last onProgress tick never arrived.
        syncedBytes: video?.fileSize ?? video?.syncedBytes ?? 0,
        remainingSeconds: undefined,
        // Keep previewUrl: the sync-center still shows the thumbnail after success. The object URL is
        // only revoked on delete (see revokeVideoUploadPreview$) or dropped on a page refresh.
      },
    );
  }),

  on(videoUploadsActions.videoUploadError, (state, { clientCode, sequenceId, videoId, error, sessionToken }) =>
    // Keep an already-known token when this error carries none (e.g. a resume whose /start failed),
    // otherwise the session would become unresumable.
    patchVideo(
      state,
      { clientCode, sequenceId, videoId },
      {
        status: ActionStatus.ERROR,
        error,
        ...(sessionToken ? { sessionToken } : {}),
      },
    ),
  ),
);
